/**
 * Bean import from a product link: TypeScript counterpart of com.pucktech.engine.beaninfo.
 * Load the page once through a PageLoader, run extractor layers in reliability order, merge field-wise with earlier
 * layers winning. Never throws.
 */
export interface BeanDraft {
  name: string | null;
  roaster: string | null;
  variety: string | null;
  region: string | null;
  process: string | null;
  producer: string | null;
  sourceUrl: string | null;
  /** URL of the label / product image (the web app stores the URL, not the bytes). */
  labelImageUrl: string | null;
  /** Plain-text product description, kept for the LLM layer. */
  description: string | null;
}

export const EMPTY_DRAFT: BeanDraft = {
  name: null, roaster: null, variety: null, region: null, process: null, producer: null,
  sourceUrl: null, labelImageUrl: null, description: null,
};

/** Loads a page body as text, or null when it cannot be fetched. Must not throw. The only I/O seam. */
export type PageLoader = (url: string) => Promise<string | null>;

export interface BeanPageExtractor {
  readonly name: string;
  /** @param soFar the draft merged from earlier layers (null for the first), so later layers can fill only gaps */
  extract(url: string, html: string, loader: PageLoader, soFar?: BeanDraft | null): Promise<Partial<BeanDraft> | null>;
}

export interface BeanInfoProvider {
  fetch(url: string): Promise<BeanDraft | null>;
}

export class WebBeanInfoProvider implements BeanInfoProvider {
  constructor(private readonly loader: PageLoader, private readonly layers: readonly BeanPageExtractor[]) {}

  async fetch(url: string): Promise<BeanDraft | null> {
    const trimmed = (url ?? "").trim();
    if (!trimmed) return null;
    let html: string | null;
    try {
      html = await this.loader(trimmed);
    } catch {
      return null;
    }
    if (html === null) return null;

    let merged: BeanDraft | null = null;
    for (const layer of this.layers) {
      let part: Partial<BeanDraft> | null;
      try {
        part = await layer.extract(trimmed, html, this.loader, merged);
      } catch {
        continue; // a broken layer never sinks the import
      }
      if (part) merged = merge(merged, part);
    }
    if (merged === null) return null;
    if (!merged.sourceUrl) merged = { ...merged, sourceUrl: trimmed };
    return merged;
  }
}

/** Field-wise merge: keep base where present, fill gaps from extra. */
export function merge(base: BeanDraft | null, extra: Partial<BeanDraft>): BeanDraft {
  const b = base ?? EMPTY_DRAFT;
  const pick = (k: keyof BeanDraft): string | null => {
    const v = b[k];
    if (v !== null && v !== undefined && v.trim() !== "") return v;
    const e = extra[k];
    return e !== undefined && e !== null && e.trim() !== "" ? e : null;
  };
  return {
    name: pick("name"), roaster: pick("roaster"), variety: pick("variety"), region: pick("region"),
    process: pick("process"), producer: pick("producer"), sourceUrl: pick("sourceUrl"),
    labelImageUrl: pick("labelImageUrl"), description: pick("description"),
  };
}

/** True when at least one user-visible field was found. */
export function hasContent(d: BeanDraft | null): d is BeanDraft {
  return d !== null && [d.name, d.roaster, d.variety, d.region, d.process, d.producer, d.labelImageUrl]
    .some((v) => v !== null && v !== "");
}
